import React, { useMemo } from "react";
import Tag from "../../ui-components/molecules/tag/tag";
import { TagColor } from "../../app/data/common/tagColor";
import { LinehaulTrip } from "../../app/data/linehaul-mileage/models";
import { TripSettlementLineItemType } from "./settlementTypes";
import { buildTripSettlementData } from "./buildTripSettlementData";
import { useDriverPayRates } from "./hooks/useDriverPayRates";

export interface TripSettlementTabProps {
  trip?: LinehaulTrip;
}

const formatCurrency = (amount: number) =>
  amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

const formatAmount = (amount: number, missingRate?: boolean) =>
  missingRate ? "—" : formatCurrency(amount);

const lineItemBadgeColor: Record<TripSettlementLineItemType, TagColor> = {
  lane: TagColor.GREY,
  exception: TagColor.ORANGE,
};

const lineItemBadgeLabel: Record<TripSettlementLineItemType, string> = {
  lane: "Lane",
  exception: "Exception",
};

export const TripSettlementTab: React.FC<TripSettlementTabProps> = ({ trip }) => {
  const driverId = trip?.driver?.driverId != null ? String(trip.driver.driverId) : "";
  const { payInfo, isLoadingPayRates, payRatesUnavailable } = useDriverPayRates(driverId);

  const settlement = useMemo(
    () => buildTripSettlementData(trip, payInfo),
    [trip, payInfo]
  );

  if (!settlement) {
    return null;
  }

  return (
    <div className="xgs-lmi__settlement">
      <div className="xgs-lmi__settlement__card">
        <div className="xgs-lmi__settlement__section-header">Compensation Breakdown</div>
        <div className="xgs-lmi__settlement__driver">
          <div className="xgs-lmi__settlement__driver-info">
            <div className="xgs-lmi__settlement__driver-name">{settlement.driverName}</div>
            <div className="xgs-lmi__settlement__driver-id">Driver ID: {settlement.driverId}</div>
          </div>
          <div className="xgs-lmi__settlement__rates">
            {isLoadingPayRates ? (
              <span className="xgs-lmi__settlement__rates-loading">Loading rates…</span>
            ) : settlement.rates.length > 0 ? (
              settlement.rates.map((rate) => (
                <Tag key={rate.label} mods={{ color: TagColor.BLUE }}>{rate.label}</Tag>
              ))
            ) : payRatesUnavailable ? (
              <span className="xgs-lmi__settlement__rates-unavailable">Rates unavailable</span>
            ) : null}
          </div>
        </div>

        {settlement.missingPayRates && (
          <div className="xgs-lmi__settlement__rates-notice">
            Pay rates were not found for this driver. Mileage pay cannot be calculated until rates are configured in the Drivers module.
          </div>
        )}

        <div className="xgs-lmi__settlement__table">
          <div className="xgs-lmi__settlement__table-header">
            <span>Description</span>
            <span>Amount</span>
          </div>

          {!settlement.lineItems.length && (
            <div className="xgs-lmi__settlement__empty">No lane or exceptions assigned yet.</div>
          )}

          {settlement.lineItems.map((item) => (
            <div key={item.id} className="xgs-lmi__settlement__row">
              <div className="xgs-lmi__settlement__description">
                <div className="xgs-lmi__settlement__description-title">
                  {item.title}
                  <Tag mods={{ color: lineItemBadgeColor[item.type] }}>
                    {lineItemBadgeLabel[item.type]}
                  </Tag>
                </div>
                {item.subtitle && (
                  <div className="xgs-lmi__settlement__description-subtitle">{item.subtitle}</div>
                )}
              </div>
              <div className="xgs-lmi__settlement__amount">
                {formatAmount(item.amount, item.type === "lane" && settlement.missingPayRates)}
              </div>
            </div>
          ))}

          <div className="xgs-lmi__settlement__summary">
            <div className="xgs-lmi__settlement__summary-row">
              <span>Mileage Pay</span>
              <span>{formatAmount(settlement.summary.mileagePay, settlement.missingPayRates)}</span>
            </div>
            <div className="xgs-lmi__settlement__summary-row">
              <span>Exception Pay</span>
              <span>{formatCurrency(settlement.summary.exceptionPay)}</span>
            </div>
            <div className="xgs-lmi__settlement__summary-row xgs-lmi__settlement__summary-row--total">
              <span>Total Settlement</span>
              <span>{formatAmount(settlement.summary.totalSettlement, settlement.missingPayRates)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
